import { useRef } from "react";
import { cx, FOCUS_RING } from "../styles";

/**
 * Two to four mutually exclusive views, side by side in a well.
 *
 * A filter, not a form field: "Active / Archived / All" above a table. It is a
 * radio group in the accessibility tree — `role="radiogroup"` with
 * `role="radio"` children — because that is exactly what it is, and announcing
 * three buttons would leave a screen-reader user with no idea that choosing one
 * unchooses the others.
 *
 * **Roving tabindex**, which is the WAI-ARIA pattern for a radio group and the
 * reason this is not three buttons. Only the selected option is in the tab
 * order; the arrows move between them. Tab therefore steps *past* the whole
 * control to the next thing on the screen, rather than through every option —
 * which on a page with four of these is the difference between two presses and
 * twelve.
 */

export interface SegmentedOption<T extends string> {
	value: T;
	label: string;
	disabled?: boolean;
	/** A tooltip, for saying why an option is disabled. */
	title?: string;
}

export interface SegmentedControlProps<T extends string> {
	options: SegmentedOption<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Names the group. Without it the control is announced with no subject. */
	"aria-label"?: string;
	"aria-labelledby"?: string;
	className?: string;
}

export function SegmentedControl<T extends string>({
	options,
	value,
	onChange,
	className,
	...labelling
}: SegmentedControlProps<T>) {
	const refs = useRef<(HTMLButtonElement | null)[]>([]);

	/** The options somebody can actually land on, with their positions. */
	function selectable() {
		return options
			.map((option, index) => ({ option, index }))
			.filter(({ option }) => option.disabled !== true);
	}

	function select(target: { option: SegmentedOption<T>; index: number } | undefined) {
		if (target === undefined) {
			return;
		}
		onChange(target.option.value);
		// Selection follows focus, so the caller sees the change immediately —
		// and focus has to follow too, or the next arrow press starts from the
		// option somebody has already moved off.
		refs.current[target.index]?.focus();
	}

	function move(from: number, step: number) {
		const enabled = selectable();
		if (enabled.length === 0) {
			return;
		}

		const current = enabled.findIndex(({ index }) => index === from);
		// Wraps, which is what the pattern specifies: pressing Right on the last
		// option goes to the first rather than doing nothing.
		select(enabled[(current + step + enabled.length) % enabled.length]);
	}

	function onKeyDown(event: React.KeyboardEvent, index: number) {
		// Read against the document, not against the key name. `ArrowRight` means
		// "towards the end" in English and "towards the start" in Arabic, and the
		// browser does not remap it — so a control that moved forward on
		// ArrowRight would run backwards for half its users.
		const rtl = document.documentElement.dir === "rtl";

		switch (event.key) {
			case "ArrowRight":
				move(index, rtl ? -1 : 1);
				break;
			case "ArrowLeft":
				move(index, rtl ? 1 : -1);
				break;
			case "ArrowDown":
				move(index, 1);
				break;
			case "ArrowUp":
				move(index, -1);
				break;
			// Home and End are absolute rather than relative, so they cannot be
			// expressed as a step from the current option — an earlier draft
			// wrote them as `move(-1, 1)` and `move(length, -1)`, and the second
			// landed on the *second-to-last* option because the starting index
			// was not in the enabled list and `findIndex` returned -1.
			case "Home":
				select(selectable()[0]);
				break;
			case "End": {
				const enabled = selectable();
				select(enabled[enabled.length - 1]);
				break;
			}
			default:
				return;
		}
		// Only for the keys that were handled, so Tab and typing are untouched.
		event.preventDefault();
	}

	const chosen = options.some((option) => option.value === value);
	const firstEnabled = options.findIndex((option) => option.disabled !== true);

	return (
		<div
			role="radiogroup"
			className={cx(
				"inline-flex gap-0.5 rounded-sm border border-border bg-bg-subtle p-0.5",
				className,
			)}
			{...labelling}
		>
			{options.map((option, index) => {
				const selected = option.value === value;
				return (
					// biome-ignore lint/a11y/useSemanticElements: a native <input type="radio"> cannot be drawn as a segment in a well — the control is a filled button with a shadow, and the radio's own box would have to be hidden and re-implemented. The role plus the roving tabindex below is the WAI-ARIA pattern for exactly this, and the keyboard behaviour is asserted in primitives.test.tsx.
					<button
						key={option.value}
						ref={(node) => {
							refs.current[index] = node;
						}}
						type="button"
						role="radio"
						aria-checked={selected}
						disabled={option.disabled}
						title={option.title}
						// The roving part: one stop for the whole group — the chosen
						// option, or the first one while nothing is chosen yet.
						tabIndex={selected || (!chosen && index === firstEnabled) ? 0 : -1}
						onClick={() => onChange(option.value)}
						onKeyDown={(event) => onKeyDown(event, index)}
						className={cx(
							"h-[26px] cursor-pointer rounded-[5px] border-none px-3 text-[12.5px] transition-colors",
							selected
								? "bg-bg-surface font-bold text-fg shadow-xs"
								: "bg-transparent font-medium text-fg-muted hover:text-fg",
							"disabled:cursor-not-allowed disabled:text-fg-subtle",
							"max-sm:min-h-12",
							FOCUS_RING,
						)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}
